import React from 'react';
import { useLocation } from '@gatsbyjs/reach-router';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'gatsby';
import { Price } from '@printdeal/product-selector';
import useSystemPages from '../../hooks/useSystemPages';
import { RootState } from '../../store';
import { DeliveryToolSettings, FileDeliverySource } from '../../types/file-delivery/FileDelivery';
import { NColorConfig } from './NColorModal';
import { dispatchDataLayerEntry, dispatchFileCheckDataLayerEvent } from '../../helpers/dataLayerHelpers/productDataLayerHelper';

interface McpAttributes {
  'Number Of Designs': number;
  'Quantity': number;
}

interface OpenPrintagonButtonProps {
  deliveryToolSettings?: DeliveryToolSettings,
  formId?: string,
  hidden?: boolean,
  selectedDeliveryTool: string,
  mcpSKU: string,
  productKey: string,
  productVersion?: number,
  mcpAttributes: string,
  productInfo?: {},
  ncolorConfig?: NColorConfig,
  children?: React.ReactNode,
  documentReference?: string | null,
  lineItemId?: string,
  reviewGroupId?: string,
  callbackUrl?: string,
  lineItemDescription?: string,
  lineItemNumber?: string,
  alternativeUploadPath?: string,
  orderId?: string,
  defaultDesignName?: string,
  contentfulProductPageId?: string,
  redirectUrl?: string,
  className?: string,
  designName?: string,
  source: FileDeliverySource,
  selectedServiceKeys?: string[],
  setStatus?: (status: 'opening-editor' | 'idle') => void,
  variantSku?: string,
  orchestrationId?: string,
  price?: Price,
}

const OpenPrintagonButton = ({
  deliveryToolSettings = {
    multipledesigns: false,
    assetTenant: undefined,
    editormode: '',
    // "Checkmatemode: checkmate" and "checkmate: on" will be renamed when MRC is replaced by the business rules app
    checkmatemode: '',
    servicekeys: '',
  },
  selectedDeliveryTool,
  mcpSKU,
  productKey,
  productVersion,
  mcpAttributes,
  productInfo,
  ncolorConfig,
  formId = 'openPrintagon',
  hidden = false,
  documentReference,
  children,
  lineItemId,
  reviewGroupId,
  callbackUrl,
  redirectUrl,
  lineItemDescription,
  lineItemNumber,
  alternativeUploadPath,
  orderId,
  defaultDesignName,
  contentfulProductPageId,
  className,
  designName,
  source,
  selectedServiceKeys,
  setStatus,
  variantSku,
  orchestrationId,
  price,
}: OpenPrintagonButtonProps) => {
  const { locale } = useIntl();

  const location = useLocation();
  const authToken = useSelector<RootState, string | undefined>(({ auth }) => auth.token);
  const mcpToken = useSelector<RootState, string | undefined>(
    ({ auth }) => auth.anonymousCOAM?.identity?.anonymousToken,
  );

  const [
    editor,
    accountDashboard,
    packagingDiy,
    uploader,
    cart,
    submitDesign,
  ] = useSystemPages(
    'editor',
    'accountDashboard',
    'packagingDIY',
    'uploader',
    'cart',
    'submitDesign',
  );
  const isEditor = (selectedDeliveryTool === 'editor');
  const dexes = ['packaging-diy'];
  // "Checkmatemode: checkmate" and "checkmate: on" will be renamed when MRC is replaced by the business rules app
  const editorType = (isEditor ? deliveryToolSettings?.editormode : deliveryToolSettings?.checkmatemode) ?? '';
  const isDex = dexes.includes(editorType);
  const multipleDesignsEnabled = deliveryToolSettings?.multipledesigns
  && (JSON.parse(mcpAttributes) as McpAttributes)?.Quantity > 1;

  const method = isEditor ? 'post' : 'get';

  const getRedirectUrl = () => {
    const redirectPath = isEditor ? submitDesign.path : cart.path;
    const path = `${orderId ? `${accountDashboard.path}/orders/${orderId}` : redirectPath}`;
    const url = new URL(`${location.origin}${path}`);

    if (lineItemNumber) {
      url.searchParams.set('lineItemNumber', lineItemNumber);
    }
    if (reviewGroupId) {
      url.searchParams.set('reviewGroupId', reviewGroupId);
    }
    if (lineItemId) {
      url.searchParams.set('lineItemId', lineItemId);
    }
    if (defaultDesignName) {
      url.searchParams.set('productName', defaultDesignName);
    }

    if (isDex || !isEditor) {
      // Design experiences and Uploader are inside Gatsby, so we can relative routing
      return `${url.pathname}${url.search}`;
    }

    // Printagon is outside Gatsby, so we need to use absolute routing
    return url.href;
  };

  const handleSubmit = () => {
    if (setStatus) setStatus('opening-editor');
    // add GA event for File Check service
    dispatchFileCheckDataLayerEvent(selectedServiceKeys);
  };

  // open Packaging Dex
  if (isDex) {
    return (
      <form id={formId} method="get" action={packagingDiy.path} className={className} onSubmit={handleSubmit}>
        <input readOnly type="hidden" name="productKey" value={productKey} />
        <input readOnly type="hidden" name="attributes" value={mcpAttributes} />
        <input readOnly type="hidden" name="redirectUrl" value={redirectUrl || getRedirectUrl()} />
        {orderId && <input readOnly type="hidden" name="orderId" value={orderId} />}
        {lineItemId && <input readOnly type="hidden" name="lineItemId" value={lineItemId} />}
        {deliveryToolSettings?.assetTenant && (
          <input readOnly type="hidden" name="assetTenant" value={deliveryToolSettings.assetTenant} />)}
        {documentReference && (<input readOnly type="hidden" name="documentReferenceUrl" value={documentReference} />)}
        {contentfulProductPageId && (
          <input readOnly type="hidden" name="contentfulProductPageId" value={contentfulProductPageId} />)}
        {children}
      </form>
    );
  }

  // open UploaderTool
  if (!isEditor) {
    const handleOpenUploaderClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      dispatchDataLayerEntry(
        'open uploader',
        'click',
      );
    };
    const uploadUrl = new URL(`${location.origin}${uploader.path}`);
    uploadUrl.searchParams.set('mcpSku', mcpSKU);
    uploadUrl.searchParams.set('mcpAttributes', mcpAttributes);
    uploadUrl.searchParams.set('multipleDesigns', String(orderId ? false : Boolean(multipleDesignsEnabled)));
    uploadUrl.searchParams.set('numberOfDesigns', String((JSON.parse(mcpAttributes) as McpAttributes)['Number Of Designs'] || 1));
    uploadUrl.searchParams.set('productKey', productKey);
    uploadUrl.searchParams.set('productName', defaultDesignName || '');

    if (orchestrationId) {
      uploadUrl.searchParams.set('orchestrationId', orchestrationId);
    }

    if (productVersion) {
      uploadUrl.searchParams.set('productVersion', String(productVersion));
    }
    uploadUrl.searchParams.set('redirectUrl', redirectUrl || getRedirectUrl());

    if (deliveryToolSettings?.servicekeys) {
      uploadUrl.searchParams.set('serviceKeys', deliveryToolSettings.servicekeys?.toUpperCase());
    }

    if (selectedServiceKeys && selectedServiceKeys.length > 0) {
      uploadUrl.searchParams.set('selectedServiceKeys', JSON.stringify(selectedServiceKeys));
    }
    uploadUrl.searchParams.set('source', source);
    uploadUrl.searchParams.set('FILE_CHECK_SERVICE_KEY', process.env.GATSBY_FILE_CHECK_SERVICE_KEY || '');

    if (documentReference) {
      uploadUrl.searchParams.set('documentReference', documentReference);
    }

    if (callbackUrl) {
      uploadUrl.searchParams.set('callbackUrl', callbackUrl);
    }

    if (lineItemNumber) {
      uploadUrl.searchParams.set('lineItemNumber', lineItemNumber);
    }

    if (lineItemDescription) {
      uploadUrl.searchParams.set('lineItemDescription', lineItemDescription);
    }

    if (contentfulProductPageId) {
      uploadUrl.searchParams.set('contentfulPageId', contentfulProductPageId);
    }

    if (variantSku) {
      uploadUrl.searchParams.set('variantSku', variantSku);
    }

    if (lineItemId) {
      uploadUrl.searchParams.set('lineItemId', lineItemId);
    }

    if (price) {
      uploadUrl.searchParams.set('priceTotalNetCentAmount', price.totalNet.centAmount.toString());
      uploadUrl.searchParams.set('priceTotalNetCurrencyCode', price.totalNet.currencyCode);
    }

    if (alternativeUploadPath) {
      uploadUrl.searchParams.set('altUploadPath', alternativeUploadPath);
    }

    return (
      <Link
        to={uploadUrl.href}
        onMouseDown={handleOpenUploaderClick}
        hidden={hidden}
      >
        {children}
      </Link>
    );
  }

  // Open Printagon / CT Editor
  function getPrintagonUrl() {
    if (process.env.GATSBY_ACTIVE_ENV === 'development') {
      return process.env.GATSBY_PRINTAGON_ENDPOINT;
    }

    return location.origin + editor.path;
  }

  return (
    <form
      id={formId}
      method={method}
      action={getPrintagonUrl()}
      hidden={hidden}
      className={className}
      onSubmit={handleSubmit}
    >
      <input readOnly type="hidden" name="storeKey" value={`${process.env.GATSBY_STORE}`} />
      <input readOnly type="hidden" name="locale" value={locale.substring(0, 2)} />
      <input readOnly type="hidden" name="authToken" value={authToken} />
      <input readOnly type="hidden" name="mcpToken" value={mcpToken} />
      <input readOnly type="hidden" name="editorType" value={editorType} />
      <input readOnly type="hidden" name="source" value={source} />
      <input readOnly type="hidden" name="mcpSku" value={mcpSKU} />
      <input readOnly type="hidden" name="variantSku" value={variantSku} />
      <input readOnly type="hidden" name="productKey" value={productKey} />
      {defaultDesignName && (<input readOnly type="hidden" name="defaultDesignName" value={`${defaultDesignName} ${new Date().toLocaleString()}`} />)}
      {/* @ts-expect-error Value gives ts error because of the boolean type */}
      <input readOnly type="hidden" name="multipleDesigns" value={orderId ? false : multipleDesignsEnabled} />
      <input readOnly type="hidden" name="mcpAttributes" value={mcpAttributes} />
      <input readOnly type="hidden" name="redirectUrl" value={redirectUrl || getRedirectUrl()} />
      <input readOnly type="hidden" name="numberOfDesigns" value={(JSON.parse(mcpAttributes) as McpAttributes)['Number Of Designs'] || 1} />
      {selectedServiceKeys && selectedServiceKeys.length > 0 && (<input readOnly type="hidden" name="selectedServiceKeys" value={JSON.stringify(selectedServiceKeys)} />)}
      {deliveryToolSettings?.servicekeys && (<input readOnly type="hidden" name="serviceKeys" value={JSON.stringify(deliveryToolSettings?.servicekeys.split(',')).toUpperCase()} />)}
      {lineItemNumber && (<input readOnly type="hidden" name="lineItemNumber" value={lineItemNumber} />)}
      {lineItemDescription && (<input readOnly type="hidden" name="lineItemDescription" value={lineItemDescription} />)}
      {callbackUrl && (<input readOnly type="hidden" name="callbackUrl" value={callbackUrl} />)}
      {deliveryToolSettings?.templateurl && <input readOnly type="hidden" name="template" value={deliveryToolSettings.templateurl} />}
      {documentReference && (<input readOnly type="hidden" name="documentReference" value={documentReference} />)}
      {productInfo && (<input readOnly type="hidden" name="productInfo" value={JSON.stringify(productInfo)} />)}
      {designName && (<input readOnly type="hidden" name="designName" value={designName} />)}
      {ncolorConfig && (
        <>
          <input readOnly type="hidden" name="uploads" value={JSON.stringify([ncolorConfig.imageUrl])} />
          <input readOnly type="hidden" name="imageColors" value={JSON.stringify(ncolorConfig.imageColors)} />
        </>
      )}
      {children}
    </form>
  );
};

export default OpenPrintagonButton;
